import React, {useEffect, useState} from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Divider,
  Grid,
  GridItem,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useModalContext,
} from '@chakra-ui/react';
import {
  useSignalBuilderNavStore,
  useSignalBuilderStore,
} from './SignalBuilder.state';
import {useCustomer, useMarkets} from '../../../hooks/api/metadata';
import Select from 'react-select';
import {
  ScoringCategories,
  ScoringCategoryLabels,
} from '../../../shared/signals';
import _ from 'lodash';
import {useSaveSignalDefinition} from '../../../hooks/api/signalDefinitions';
import {ObjectId} from 'bson';
import {RangeSignalBuilder} from './RangeSignalBuilder';
import {Label, LabelInput} from './helpers';

import {BooleanSignalBuilder} from './BooleanSignalBuilder';
import {CategorySignalBuilder} from './CategorySignalBuilder';
import {PicklistSignalBuilder} from './PicklistSignalBuilder';
import {
  AccountFieldOrCustomField,
  getIsFieldSignalFn,
  getSignalEvalFnInputs,
  isCreatableSignalDefinitionType,
  SignalDefinition,
  SignalEvalFn,
} from '../../../shared/signalDefinition';
import {
  CustomSignalFields,
  isAccountField,
} from '../../../shared/fields/keyplay';
import {useFieldResolver, useResolvedField} from '../../../hooks/fields';
import {filterNull} from '../../../shared/util';
import {AccountFieldToSignalGroup} from '../../../shared/signalGroups';
import {isRestrictedField} from '../../../shared/fields/types';
import {useIsKeyplayAdmin} from '../../../hooks/api/user';
import {getSignalsInMarket} from '../../../shared/market';
import {captureMessage} from '@sentry/react';

export const SaveSignalModal = ({
  isOpen,
  onClose,
  signalDefinition,
}: {
  isOpen: boolean;
  onClose: () => void;
  signalDefinition?: SignalDefinition;
}) => {
  const {setStep} = useSignalBuilderNavStore();
  const {reset} = useSignalBuilderStore();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={() => {
        reset();
        setStep('selectField');
      }}
      isCentered
      size="3xl"
    >
      <ModalOverlay />
      <SaveSignalModalContent signalDefinition={signalDefinition} />
    </Modal>
  );
};

/**
 * Checks if a evalFn can be edited in the signal builder UI.
 * Only supports signal definitions that can be created using the signal builder.
 */
export function getEditableSignalData(
  signalDefinition: SignalDefinition | undefined
) {
  if (!signalDefinition) {
    return null;
  }
  const inputs = getSignalEvalFnInputs(signalDefinition.evalFn);

  if (
    inputs.length === 1 &&
    inputs[0].type === 'field' &&
    isCreatableSignalDefinitionType(signalDefinition.evalFn.type)
  ) {
    return {
      field: inputs[0].field,
      evalFn: signalDefinition.evalFn,
    };
  }

  return null;
}

const SaveSignalModalContent = ({
  signalDefinition,
}: {
  signalDefinition?: SignalDefinition;
}) => {
  const {step, setStep} = useSignalBuilderNavStore();
  const {reset, setLabel, setSignalCategory, setEvalFn} =
    useSignalBuilderStore();
  const [selectedField, setSelectedField] =
    useState<AccountFieldOrCustomField | null>(null);

  // Initialize the form with existing signal data when editing
  useEffect(() => {
    if (!signalDefinition) {
      return;
    }

    // Check if evalFn editing is supported and get the field/evalFn data
    const editableData = getEditableSignalData(signalDefinition);
    if (editableData) {
      // For evalFn editable signals, set up the field and go to defineSignal step
      setSelectedField(editableData.field);
      setEvalFn(editableData.evalFn);
      setStep('defineSignal');
    } else {
      // For basic editable signals, set up the basic properties and go to metadata edit step
      setStep('editMetadata');
    }
    // Common to both cases
    setLabel(signalDefinition.label);
    setSignalCategory(signalDefinition.category);
  }, [signalDefinition, setLabel, setSignalCategory, setEvalFn, setStep]);

  return (
    <ModalContent>
      <ModalCloseButton />
      {step === 'selectField' && (
        <SelectFieldStep
          selectedField={selectedField}
          onFieldSelected={(selectedField) => {
            setSelectedField(selectedField);
            reset();
          }}
        />
      )}
      {step === 'defineSignal' && !!selectedField && (
        <DefineSignalStep
          field={selectedField}
          signalDefinition={signalDefinition}
        />
      )}
      {step === 'editMetadata' && signalDefinition && (
        <EditMetadataStep signalDefinition={signalDefinition} />
      )}
    </ModalContent>
  );
};

const SelectFieldStep = ({
  selectedField,
  onFieldSelected,
}: {
  selectedField: AccountFieldOrCustomField | null;
  onFieldSelected: (field: AccountFieldOrCustomField) => void;
}) => {
  const {setStep} = useSignalBuilderNavStore();
  const fieldResolver = useFieldResolver();
  const customer = useCustomer();
  const dataType = selectedField
    ? fieldResolver(selectedField)?.dataType
    : null;

  const customerFields = (customer.fieldDefinitions ?? [])
    .filter((field) => !isRestrictedField(field))
    .filter(
      ({dataType, type, config}) =>
        dataType === 'number' ||
        dataType === 'boolean' ||
        dataType === 'picklist' ||
        dataType === 'multiPicklist' ||
        (dataType === 'string' &&
          type === 'ai' &&
          config.fieldType === 'category')
    )
    .filter(({status}) => status === 'published');

  const resolvedFieldOptions = filterNull(
    [...CustomSignalFields, ...customerFields.map(({id}) => id)].map(
      (field) => {
        const resolvedField = fieldResolver(field);
        if (!resolvedField) {
          return null;
        }

        return {
          value: field,
          label: resolvedField.label,
          source: resolvedField.source,
        };
      }
    )
  ).sort((a, b) => a.label.localeCompare(b.label));

  const groupedFieldOptions = [
    {
      label: 'Custom',
      options: resolvedFieldOptions.filter(({source}) => source === 'custom'),
    },
    {
      label: 'Keyplay',
      options: resolvedFieldOptions.filter(({source}) => source === 'keyplay'),
    },
  ];

  return (
    <>
      <ModalHeader fontWeight="normal">
        <Text fontSize="xl" mb="1">
          Select Signal Source
        </Text>
        <Text fontSize="sm" textColor="kgray.300">
          Learn more about how to create custom signals{' '}
          <Link
            href="https://docs.keyplay.io/en/"
            isExternal
            textColor="kblue.300"
          >
            here
          </Link>
          .
        </Text>
      </ModalHeader>
      <ModalBody minH="300px">
        <Grid
          templateColumns="1fr 200px"
          columnGap="8"
          rowGap="2"
          alignItems="baseline"
        >
          <GridItem>
            <Label>Field</Label>
          </GridItem>
          <GridItem>
            <Label>Field Type</Label>
          </GridItem>

          <GridItem>
            <Select
              isMulti={false}
              onChange={(newValue) => {
                if (!newValue) {
                  return;
                }

                onFieldSelected(newValue.value);
              }}
              options={groupedFieldOptions}
              placeholder="Select a Field"
              value={resolvedFieldOptions.find(
                ({value}) => value.toString() === selectedField?.toString()
              )}
            />
          </GridItem>
          <GridItem>{dataType ?? '-'}</GridItem>
        </Grid>
      </ModalBody>
      <ModalFooter
        borderTop="1px solid"
        borderColor="kgray.200"
        justifyContent="start"
      >
        <Button
          colorScheme="kbuttonblue"
          fontWeight="normal"
          isDisabled={!selectedField}
          onClick={() => setStep('defineSignal')}
        >
          Next
        </Button>
      </ModalFooter>
    </>
  );
};

const categoryOptions = ScoringCategories.map((category) => ({
  value: category,
  label: ScoringCategoryLabels[category],
}));

function getSignalGroup({
  field,
  signalDefinitions,
}: {
  evalFn: SignalEvalFn;
  field: AccountFieldOrCustomField;
  signalDefinitions: SignalDefinition[];
}) {
  // if we're creating a signal for a single Keyplay field, prefer the built-in group for that field
  let accountFieldGroup;
  if (isAccountField(field)) {
    accountFieldGroup =
      AccountFieldToSignalGroup[
        field as keyof typeof AccountFieldToSignalGroup
      ];
  }

  // otherwise if there's an existing signal definition that matches the field
  // of the new signal definition, use that
  const isFieldSignalDefinition = getIsFieldSignalFn(field);
  const signalDefinitionGroup = signalDefinitions.find(
    isFieldSignalDefinition
  )?.group;

  return accountFieldGroup ?? signalDefinitionGroup ?? new ObjectId();
}

const SignalBuilderSelector = ({
  field,
  signalDefinition,
  isEditingDisabled,
}: {
  field: AccountFieldOrCustomField;
  signalDefinition?: SignalDefinition;
  isEditingDisabled: boolean;
}) => {
  const evalFnType = signalDefinition?.evalFn.type;
  const {dataType} = useResolvedField(field);

  // Show signal builder based on existing evalFn type when editing (it is
  // possible to create a signal with different evalFn types than the field's
  // data type via script), or field dataType when creating
  if (evalFnType === 'range' || (!signalDefinition && dataType === 'number')) {
    return <RangeSignalBuilder field={field} isDisabled={isEditingDisabled} />;
  }
  if (
    evalFnType === 'isTrue' ||
    evalFnType === 'isFalse' ||
    (!signalDefinition && dataType === 'boolean')
  ) {
    return (
      <BooleanSignalBuilder field={field} isDisabled={isEditingDisabled} />
    );
  }
  if (dataType === 'picklist' || dataType === 'multiPicklist') {
    return (
      <PicklistSignalBuilder field={field} isDisabled={isEditingDisabled} />
    );
  }
  if (
    evalFnType === 'equal' ||
    evalFnType === 'in' ||
    (!signalDefinition && dataType === 'string')
  ) {
    return (
      <CategorySignalBuilder field={field} isDisabled={isEditingDisabled} />
    );
  }

  // Log to Sentry and display error message for unsupported signal types
  captureMessage(`Unsupported signal type: ${evalFnType || dataType}`, {
    level: 'error',
    extra: {
      evalFnType,
      dataType,
      field,
      signalDefinition: signalDefinition?.id,
    },
  });

  // Display error message in modal
  return (
    <GridItem colSpan={2}>
      <Alert status="warning">
        <AlertIcon />
        <AlertDescription>
          Unsupported signal type. Please contact Keyplay support.
        </AlertDescription>
      </Alert>
    </GridItem>
  );
};

const useIsEditingDisabled = (signalDefinition?: SignalDefinition) => {
  const markets = useMarkets();
  const isKeyplayAdmin = useIsKeyplayAdmin();
  // Check if the signal is used in any market and get the list of markets
  const marketsUsingSignal = signalDefinition
    ? markets.filter((market) =>
        getSignalsInMarket(market).includes(signalDefinition.id)
      )
    : [];
  // Signal label can always be edited, but other fields are disabled if signal is used and user is not admin
  const isEditingDisabled = marketsUsingSignal.length > 0 && !isKeyplayAdmin;

  return {
    isEditingDisabled,
    marketsUsingSignal,
  };
};

const DefineSignalStep = ({
  field,
  signalDefinition,
}: {
  field: AccountFieldOrCustomField;
  signalDefinition?: SignalDefinition;
}) => {
  const {setStep} = useSignalBuilderNavStore();
  const {evalFn, label, setSignalCategory, signalCategory} =
    useSignalBuilderStore();
  const {label: fieldLabel} = useResolvedField(field);
  const {signalDefinitions} = useCustomer();
  const saveSignalDefinitionMutation = useSaveSignalDefinition();
  const {isEditingDisabled, marketsUsingSignal} =
    useIsEditingDisabled(signalDefinition);
  const {onClose} = useModalContext();

  const saveSignalDefinition = () => {
    if (!signalCategory || !label || !evalFn) {
      return;
    }

    const group = getSignalGroup({
      evalFn,
      field,
      signalDefinitions: signalDefinitions ?? [],
    });

    saveSignalDefinitionMutation.mutate(
      {
        ...(signalDefinition?.id ? {id: signalDefinition.id} : {}),
        label,
        category: signalCategory,
        evalFn,
        group,
      },
      {onSuccess: onClose}
    );
  };

  return (
    <>
      <ModalHeader fontWeight="normal">
        <Text fontSize="xl" mb="1">
          {signalDefinition ? 'Edit Signal' : 'Define Signal'} - {fieldLabel}
        </Text>
        <Text fontSize="sm" textColor="kgray.300">
          Learn more about how to create custom signals{' '}
          <Link
            href="https://docs.keyplay.io/en/"
            isExternal
            textColor="kblue.300"
          >
            here
          </Link>
          .
        </Text>
        {isEditingDisabled && (
          <Text fontSize="sm" textColor="kgray.300" mt="4">
            Signal definition fields are disabled because this signal is used in
            the following markets:{' '}
            {marketsUsingSignal.map(({label}) => label).join(', ')}. Only the
            signal label can be edited.
          </Text>
        )}
      </ModalHeader>
      <ModalBody minH="300px">
        <Grid columnGap="8" rowGap="2" templateColumns="60% 1fr">
          <GridItem>
            <Label>Category</Label>
          </GridItem>
          <GridItem />
          <GridItem>
            <Select
              isDisabled={isEditingDisabled}
              isMulti={false}
              onChange={(newValue) => {
                if (!newValue) {
                  return;
                }

                setSignalCategory(newValue.value);
              }}
              options={categoryOptions}
              placeholder="Select a category"
              value={categoryOptions.find(
                ({value}) => value === signalCategory
              )}
            />
          </GridItem>
          <GridItem />

          <GridItem colSpan={2}>
            <Divider my="5" />
          </GridItem>

          <GridItem>
            <Label>Signal</Label>
          </GridItem>
          <GridItem>
            <Label>Label</Label>
          </GridItem>

          <SignalBuilderSelector
            field={field}
            signalDefinition={signalDefinition}
            isEditingDisabled={isEditingDisabled}
          />
        </Grid>
      </ModalBody>
      <ModalFooter
        borderTop="1px solid"
        borderColor="kgray.200"
        justifyContent="start"
        gap="2"
      >
        <Button
          colorScheme="kbuttonblue"
          fontWeight="normal"
          isDisabled={!signalCategory || !label || !evalFn}
          isLoading={saveSignalDefinitionMutation.isPending}
          onClick={saveSignalDefinition}
        >
          Save Signal
        </Button>
        {!signalDefinition && (
          <Button
            fontWeight="normal"
            onClick={() => setStep('selectField')}
            variant="ghost"
          >
            Back
          </Button>
        )}
      </ModalFooter>
    </>
  );
};

const EditMetadataStep = ({
  signalDefinition,
}: {
  signalDefinition: SignalDefinition;
}) => {
  const {label, setSignalCategory, signalCategory} = useSignalBuilderStore();
  const saveSignalDefinitionMutation = useSaveSignalDefinition();
  const {isEditingDisabled} = useIsEditingDisabled(signalDefinition);
  const {onClose} = useModalContext();

  const saveSignalDefinition = () => {
    if (!signalCategory || !label) {
      return;
    }

    saveSignalDefinitionMutation.mutate(
      {
        id: signalDefinition.id,
        label,
        category: signalCategory,
        evalFn: signalDefinition.evalFn, // Keep the existing evaluation function
        group: signalDefinition.group,
      },
      {onSuccess: onClose}
    );
  };

  return (
    <>
      <ModalHeader fontWeight="normal">
        <Text fontSize="xl" mb="1">
          Edit Signal
        </Text>
        <Text fontSize="sm" textColor="kgray.300">
          You can edit the label and category for this signal. The evaluation
          logic cannot be modified.
        </Text>
      </ModalHeader>
      <ModalBody minH="300px">
        <Grid columnGap="8" rowGap="2" templateColumns="60% 1fr">
          <GridItem>
            <Label>Category</Label>
          </GridItem>
          <GridItem />
          <GridItem>
            <Select
              isDisabled={isEditingDisabled}
              isMulti={false}
              options={categoryOptions}
              value={categoryOptions.find(
                (option) => option.value === signalCategory
              )}
              onChange={(newValue) => {
                if (!newValue) {
                  return;
                }
                setSignalCategory(newValue.value);
              }}
            />
          </GridItem>
          <GridItem />

          <GridItem mt="8">
            <Label>Label</Label>
          </GridItem>
          <GridItem />
          <GridItem>
            <LabelInput />
          </GridItem>
        </Grid>
      </ModalBody>
      <ModalFooter
        borderTop="1px solid"
        borderColor="kgray.200"
        justifyContent="start"
        gap="2"
      >
        <Button
          colorScheme="kbuttonblue"
          fontWeight="normal"
          isDisabled={!signalCategory || !label}
          isLoading={saveSignalDefinitionMutation.isPending}
          onClick={saveSignalDefinition}
        >
          Save Signal
        </Button>
      </ModalFooter>
    </>
  );
};
